import React, { useState, useRef, useEffect, useCallback, useMemo, useImperativeHandle } from 'react';
import toast from 'react-hot-toast';
import { useEditorStore, CropState, FilterState } from '../../store/editorStore';
import { photosApi } from '../../api/photos';
import { EXPORT_TARGETS, PLATFORMS } from '../../constants/exportTargets';
import { ExportTarget } from '../../types';
import { PlatformIcon, PLATFORM_COLORS } from '../UI/PlatformIcon';
import { track, EVENTS } from '../../lib/mixpanel';
import { Move, Grid3X3, SlidersHorizontal } from 'lucide-react';
import { drawCoverFit, renderToTarget, buildSourceCanvas as buildSourceCanvasFromUrl } from '../../lib/exportRender';
import styles from './ExportPanel.module.css';

interface ExportPanelProps {
  photoFilename: string;
  photoId: string;
  canvasWidth: number;
  /** Called once after any successful export (single format or ZIP). */
  onExported?: () => void;
}

// ── Helpers ──────────────────────────────────────────────────────────

function buildSourceCanvas(
  photoId: string,
  filters: FilterState,
  crop: CropState,
  canvasWidth: number,
): Promise<HTMLCanvasElement> {
  return buildSourceCanvasFromUrl(photosApi.originalUrl(photoId), filters, crop, canvasWidth);
}

// ── Format preview card with canvas + drag/touch reframe + grid + sliders ──

const PREVIEW_MAX_W = 240;

const FOCUS_PRESETS: { label: string; focusX: number; focusY: number }[] = [
  { label: '\u2196', focusX: 0,   focusY: 0   },
  { label: '\u2191', focusX: 0.5, focusY: 0   },
  { label: '\u2197', focusX: 1,   focusY: 0   },
  { label: '\u2190', focusX: 0,   focusY: 0.5 },
  { label: '\u2022', focusX: 0.5, focusY: 0.5 },
  { label: '\u2192', focusX: 1,   focusY: 0.5 },
  { label: '\u2199', focusX: 0,   focusY: 1   },
  { label: '\u2193', focusX: 0.5, focusY: 1   },
  { label: '\u2198', focusX: 1,   focusY: 1   },
];

type ReframeMode = 'grid' | 'sliders';

interface FormatCardProps {
  target: ExportTarget;
  formatKey: string;
  sourceCanvas: HTMLCanvasElement | null;
  checked: boolean;
  onToggle: () => void;
  onExportSingle: () => void;
  exporting: boolean;
}

const FormatCard: React.FC<FormatCardProps> = ({
  target,
  formatKey,
  sourceCanvas,
  checked,
  onToggle,
  onExportSingle,
  exporting,
}) => {
  const { getReframeOffset, setReframeOffset } = useEditorStore();
  const offset = getReframeOffset(formatKey);
  const [reframeMode, setReframeMode] = useState<ReframeMode>('grid');

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dragging = useRef(false);
  const startPos = useRef({ x: 0, y: 0, focusX: 0.5, focusY: 0.5 });

  const aspect = target.width / target.height;
  let previewW: number, previewH: number;
  if (aspect >= 1) {
    previewW = PREVIEW_MAX_W;
    previewH = Math.round(PREVIEW_MAX_W / aspect);
  } else {
    previewH = Math.min(PREVIEW_MAX_W, 300);
    previewW = Math.round(previewH * aspect);
  }

  const slackInfo = useMemo(() => {
    if (!sourceCanvas) return { slackX: 0, slackY: 0 };
    const coverScale = Math.max(previewW / sourceCanvas.width, previewH / sourceCanvas.height);
    const drawW = sourceCanvas.width * coverScale;
    const drawH = sourceCanvas.height * coverScale;
    return { slackX: drawW - previewW, slackY: drawH - previewH };
  }, [sourceCanvas, previewW, previewH]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !sourceCanvas) return;
    const ctx = canvas.getContext('2d')!;
    drawCoverFit(ctx, sourceCanvas, previewW, previewH, offset.focusX, offset.focusY);
  }, [sourceCanvas, offset.focusX, offset.focusY, previewW, previewH]);

  const applyDrag = useCallback((clientX: number, clientY: number) => {
    if (!dragging.current) return;
    const dx = clientX - startPos.current.x;
    const dy = clientY - startPos.current.y;
    let newFocusX = startPos.current.focusX;
    let newFocusY = startPos.current.focusY;
    if (slackInfo.slackX > 1) newFocusX = startPos.current.focusX - dx / slackInfo.slackX;
    if (slackInfo.slackY > 1) newFocusY = startPos.current.focusY - dy / slackInfo.slackY;
    newFocusX = Math.max(0, Math.min(1, newFocusX));
    newFocusY = Math.max(0, Math.min(1, newFocusY));
    setReframeOffset(formatKey, { focusX: newFocusX, focusY: newFocusY });
  }, [formatKey, slackInfo, setReframeOffset]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      dragging.current = true;
      startPos.current = { x: e.clientX, y: e.clientY, focusX: offset.focusX, focusY: offset.focusY };
      e.preventDefault();
    },
    [offset.focusX, offset.focusY],
  );

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => applyDrag(e.clientX, e.clientY);
    const handleMouseUp = () => { dragging.current = false; };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [applyDrag]);

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length !== 1) return;
      dragging.current = true;
      const t = e.touches[0];
      startPos.current = { x: t.clientX, y: t.clientY, focusX: offset.focusX, focusY: offset.focusY };
    },
    [offset.focusX, offset.focusY],
  );

  useEffect(() => {
    const handleTouchMove = (e: TouchEvent) => {
      if (!dragging.current || e.touches.length !== 1) return;
      e.preventDefault();
      applyDrag(e.touches[0].clientX, e.touches[0].clientY);
    };
    const handleTouchEnd = () => { dragging.current = false; };
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd);
    return () => {
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [applyDrag]);

  const hasReframe = offset.focusX !== 0.5 || offset.focusY !== 0.5;

  const activePresetIdx = FOCUS_PRESETS.findIndex(
    (p) => Math.abs(p.focusX - offset.focusX) < 0.05 && Math.abs(p.focusY - offset.focusY) < 0.05,
  );

  return (
    <div className={[styles['format-card'], checked ? styles['format-card-checked'] : ''].filter(Boolean).join(' ')}>
      {/* Preview canvas */}
      <div className={styles['preview-container']}>
        <div className={`${styles['preview-wrapper']} ${styles['preview-dynamic']}`} style={{ '--preview-w': `${previewW}px`, '--preview-h': `${previewH}px` } as React.CSSProperties}>
          <canvas
            ref={canvasRef}
            width={previewW}
            height={previewH}
            className={`${styles['preview-canvas']} ${styles['canvas-dynamic']}`}
            style={{ '--canvas-w': `${previewW}px`, '--canvas-h': `${previewH}px` } as React.CSSProperties}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
          />
          {!hasReframe && (
            <div className={styles['drag-hint']}>
              <div className={styles['drag-hint-pill']}>
                <Move className={styles['icon-xs']} strokeWidth={2.5} />
                Drag to reframe
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile reframe controls */}
      {checked && (slackInfo.slackX > 1 || slackInfo.slackY > 1) && (
        <div className={styles['reframe-controls']}>
          <div className={styles['reframe-header']}>
            <p className={styles['reframe-label']}>Reframe</p>
            <div className={styles['mode-toggle']}>
              <button
                onClick={() => setReframeMode('grid')}
                className={[styles['mode-btn'], reframeMode === 'grid' ? styles['mode-btn-active'] : ''].filter(Boolean).join(' ')}
                title="Grid presets"
              >
                <Grid3X3 className={styles['icon-xs']} />
              </button>
              <button
                onClick={() => setReframeMode('sliders')}
                className={[styles['mode-btn'], reframeMode === 'sliders' ? styles['mode-btn-active'] : ''].filter(Boolean).join(' ')}
                title="Fine-tune sliders"
              >
                <SlidersHorizontal className={styles['icon-xs']} />
              </button>
            </div>
          </div>

          {reframeMode === 'grid' ? (
            <div className={styles['focus-grid']}>
              {FOCUS_PRESETS.map((preset, i) => (
                <button
                  key={i}
                  onClick={() => setReframeOffset(formatKey, { focusX: preset.focusX, focusY: preset.focusY })}
                  className={[styles['focus-btn'], activePresetIdx === i ? styles['focus-btn-active'] : ''].filter(Boolean).join(' ')}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          ) : (
            <div className={styles['slider-group']}>
              <div>
                <div className={styles['slider-row-header']}>
                  <span className={styles['slider-row-label']}>Horizontal</span>
                  <span className={styles['slider-row-value']}>{Math.round(offset.focusX * 100)}%</span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.01}
                  value={offset.focusX}
                  onChange={(e) => setReframeOffset(formatKey, { ...offset, focusX: parseFloat(e.target.value) })}
                  className={styles['range-input']}
                />
              </div>
              <div>
                <div className={styles['slider-row-header']}>
                  <span className={styles['slider-row-label']}>Vertical</span>
                  <span className={styles['slider-row-value']}>{Math.round(offset.focusY * 100)}%</span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.01}
                  value={offset.focusY}
                  onChange={(e) => setReframeOffset(formatKey, { ...offset, focusY: parseFloat(e.target.value) })}
                  className={styles['range-input']}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Controls */}
      <div className={styles['card-footer']}>
        <label className={styles['checkbox-label']}>
          <input
            type="checkbox"
            checked={checked}
            onChange={onToggle}
            className={styles['checkbox']}
          />
          <div className={styles['checkbox-content']}>
            <p className={styles['format-name']}>
              {target.platform} — {target.label}
            </p>
            <p className={styles['format-dims']}>{target.width} x {target.height}</p>
          </div>
        </label>
        <div className={styles['card-actions']}>
          {hasReframe && (
            <button
              onClick={() => setReframeOffset(formatKey, { focusX: 0.5, focusY: 0.5 })}
              className={styles['reset-btn']}
              title="Reset reframe to center"
            >
              Reset
            </button>
          )}
          <button
            onClick={onExportSingle}
            disabled={exporting}
            className={styles['export-single-btn']}
          >
            Export
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Export panel ──────────────────────────────────────────────────────

export interface ExportPanelHandle {
  /** Resolves true if at least one file was exported successfully. */
  exportAll: () => Promise<boolean>;
  exporting: boolean;
  sourceReady: boolean;
}

export const ExportPanel = React.forwardRef<ExportPanelHandle, ExportPanelProps>(({
  photoFilename,
  photoId,
  canvasWidth,
  onExported,
}, ref) => {
  const {
    filters,
    crop,
    overlayConfig,
    brandingOverlayConfig,
    overlays,
    selectedPlatforms,
    selectedFormats,
    exportName,
    setSelectedPlatforms,
    toggleFormat,
    setSelectedFormats,
    setExportName,
    getReframeOffset,
  } = useEditorStore();

  const [exporting, setExporting] = useState(false);
  const [sourceCanvas, setSourceCanvas] = useState<HTMLCanvasElement | null>(null);
  const [buildingSource, setBuildingSource] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setBuildingSource(true);
    buildSourceCanvas(photoId, filters, crop, canvasWidth)
      .then((canvas) => {
        if (!cancelled) setSourceCanvas(canvas);
      })
      .catch(() => {
        if (!cancelled) toast.error('Failed to prepare image for export');
      })
      .finally(() => {
        if (!cancelled) setBuildingSource(false);
      });
    return () => { cancelled = true; };
  }, [photoId, filters, crop, canvasWidth]);

  const togglePlatform = (platform: string) => {
    const platformFormatKeys = EXPORT_TARGETS
      .filter((t) => t.platform === platform)
      .map((t) => `${t.platform}-${t.label}`);

    const enabling = !selectedPlatforms.includes(platform);
    track(EVENTS.EXPORT_PLATFORM_TOGGLED, { platform, enabled: enabling });

    if (!enabling) {
      setSelectedPlatforms(selectedPlatforms.filter((p) => p !== platform));
      // Also drop this platform's formats so they aren't silently exported later.
      setSelectedFormats(selectedFormats.filter((f) => !platformFormatKeys.includes(f)));
    } else {
      setSelectedPlatforms([...selectedPlatforms, platform]);
      for (const key of platformFormatKeys) {
        if (!selectedFormats.includes(key)) toggleFormat(key);
      }
    }
  };

  const handleExportSingle = async (target: ExportTarget) => {
    if (!sourceCanvas) return;
    const baseName = exportName.trim() || photoFilename.replace(/\.[^.]+$/, '');
    const key = `${target.platform}-${target.label}`;
    const reframe = getReframeOffset(key);

    setExporting(true);
    try {
      const blob = await renderToTarget(target, sourceCanvas, overlayConfig, overlays, reframe, brandingOverlayConfig);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = `${baseName}-${target.platform}-${target.label}-${target.width}x${target.height}.jpg`;
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
      track(EVENTS.PHOTO_EXPORTED, {
        export_type: 'single',
        platform: target.platform,
        format: target.label,
        resolution: `${target.width}x${target.height}`,
      });
      toast.success('Downloaded!');
      onExported?.();
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleExportAll = useCallback(async (): Promise<boolean> => {
    if (!sourceCanvas) return false;
    const targets = EXPORT_TARGETS.filter((t) => selectedFormats.includes(`${t.platform}-${t.label}`));
    if (targets.length === 0) {
      toast.error('Select at least one format to export');
      return false;
    }

    const baseName = exportName.trim() || photoFilename.replace(/\.[^.]+$/, '');
    setExporting(true);
    try {
      const { default: JSZip } = await import('jszip');
      const zip = new JSZip();
      for (const target of targets) {
        const key = `${target.platform}-${target.label}`;
        const reframe = getReframeOffset(key);
        const blob = await renderToTarget(target, sourceCanvas, overlayConfig, overlays, reframe, brandingOverlayConfig);
        zip.file(`${baseName}-${target.platform}-${target.label}-${target.width}x${target.height}.jpg`, blob);
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${baseName}-exports.zip`;
      link.click();
      URL.revokeObjectURL(url);
      track(EVENTS.PHOTO_EXPORTED, {
        export_type: 'zip',
        format_count: targets.length,
        platforms: [...new Set(targets.map((t) => t.platform))],
        formats: targets.map((t) => `${t.platform}-${t.label}`),
      });
      toast.success(`Exported ${targets.length} files!`);
      onExported?.();
      return true;
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Export failed');
      return false;
    } finally {
      setExporting(false);
    }
  }, [sourceCanvas, selectedFormats, exportName, photoFilename, overlayConfig, overlays, getReframeOffset, onExported]);

  useImperativeHandle(ref, () => ({
    exportAll: handleExportAll,
    exporting,
    sourceReady: !buildingSource && sourceCanvas !== null,
  }), [handleExportAll, exporting, buildingSource, sourceCanvas]);

  return (
    <div className={styles['container']}>
      <h3 className={styles['title']}>Export for Social Media</h3>

      {/* Export name */}
      <div>
        <label className={styles['label']}>Export Name</label>
        <input
          type="text"
          value={exportName}
          onChange={(e) => setExportName(e.target.value)}
          placeholder="Enter export name..."
          className={styles['name-input']}
        />
        <p className={styles['name-hint']}>Used as filename prefix for downloads</p>
      </div>

      {/* Platform toggles */}
      <div>
        <p className={styles['section-label']}>Platforms</p>
        <div className={styles['platform-grid']}>
          {PLATFORMS.map((platform) => {
            const active = selectedPlatforms.includes(platform);
            return (
              <button
                key={platform}
                onClick={() => togglePlatform(platform)}
                className={[
                  styles['platform-btn'],
                  active ? styles['platform-btn-active'] : '',
                ].filter(Boolean).join(' ')}
              >
                <span className={active ? '' : `${styles['platform-icon-inactive']} ${styles['platform-icon-color']}`} style={active ? undefined : { '--platform-color': PLATFORM_COLORS[platform] } as React.CSSProperties}>
                  <PlatformIcon platform={platform} className={styles['platform-icon']} />
                </span>
                {platform}
              </button>
            );
          })}
        </div>
      </div>

      {/* Loading source */}
      {buildingSource && (
        <div className={styles['loading-text']}>Preparing previews...</div>
      )}

      {/* Format previews */}
      {!buildingSource && selectedPlatforms.length > 0 && (
        <div className={styles['container']}>
          <p className={styles['hint-text']}>
            Drag each preview to reframe the subject for that format. Overlay is applied on the final export.
          </p>
          {selectedPlatforms.map((platform) => {
            const targets = EXPORT_TARGETS.filter((t) => t.platform === platform);
            return (
              <div key={platform}>
                <p className={styles['platform-group-title']}>{platform}</p>
                <div className={styles['format-stack']}>
                  {targets.map((target) => {
                    const key = `${target.platform}-${target.label}`;
                    return (
                      <FormatCard
                        key={key}
                        target={target}
                        formatKey={key}
                        sourceCanvas={sourceCanvas}
                        checked={selectedFormats.includes(key)}
                        onToggle={() => toggleFormat(key)}
                        onExportSingle={() => handleExportSingle(target)}
                        exporting={exporting}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
});
